import React, { useState } from "react";
import { Pencil, MapPin, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { apiSendEmail } from "../../../services/contact";
import { notification, Spin } from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { path } from "../../../utils/common/constant";

const ContactInfo = () => {
  const { t } = useTranslation();
  const fadeInUp = {
    hidden: { opacity: 0, y: 100 },
    visible: { opacity: 1, y: 0 },
  };
  const contactMethods = [
    {
      icon: <Pencil className="w-8 h-8" />,
      title: "ContactEmailTitle",
      children: [
        // {
        //   content: "investmentteam@idg-global.com",
        //   link: "mailto:investmentteam@idg-global.com",
        // },
        {
          content: "info@sunnypearl.com.vn",
          link: "mailto:info@sunnypearl.com.vn",
        },
        {
          content: "info@idg-global.com",
          link: "mailto:info@idg-global.com",
        },
      ],
    },
    {
      icon: <MapPin className="w-8 h-8" />,
      title: "ContactAddressTitle",
      children: [
        {
          content: "ContactAddressContent_01",
          link: "https://maps.app.goo.gl/heNqyJ2BYMHpycvQ6",
        },
      ],
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: "ContactPhoneTitle",
      children: [
        // {
        //   content: "+ 84 93 178 00 86",
        //   link: "tel:+84931780086",
        // },
        { content: "+ 84 93 88 99 017", link: "tel:+84938899017" },
        // { content: "+ 61 481 736 827", link: "tel:+61481736827" },
      ],
    },
  ];

  return (
    <div className="flex bg-[#f8f8f8] items-center justify-center">
      <div className="flex flex-col lg:flex-row justify-between gap-12 lg:gap-8 w-[80%] py-[4.375rem] max-w-[1140px] pvmax:max-dv:w-[85%] qhd:max-w-[60%]">
        {contactMethods.map((method, contactIndex) => (
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView={"visible"}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, delay: contactIndex * 0.1 }}
            key={contactIndex}
            className={`flex items-start ph:w-[350px] w-[350px] ${
              contactIndex === 2 ? "lg:justify-end" : ""
            } ${contactIndex === 1 ? "xl:ml-28 lg:ml-14" : ""}`}
          >
            <div className="bg-primary text-white text-2xl p-[19px] rounded-xl">
              <span className="">{method.icon}</span>
            </div>
            <div className="pl-5">
              <h3 className="text-lg mb-1 text-primary font-semibold ">
                {t(method.title)}
              </h3>
              <div>
                {method?.children?.map((child, index) => {
                  if (contactIndex === 1) {
                    return (
                      <a
                        key={index}
                        href={method.link}
                        rel="noreferrer"
                        target="_blank"
                        className="text-primary pt-2 hover:text-gray-800 transition-colors "
                      >
                        {t(child.content)}
                      </a>
                    );
                  } else {
                    return (
                      <div className="flex flex-col">
                        <a
                          key={index}
                          href={child.link}
                          className="text-primary underline pt-2 hover:text-gray-800 transition-colors "
                        >
                          {t(child.content)}
                        </a>
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const ContactForm = () => {
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };
  const { t } = useTranslation();
  const features = [
    // t("ContactFormFeature_1"),
    t("ContactFormFeature_2"),
    t("ContactFormFeature_3"),
    t("ContactFormFeature_4"),
  ];

  const [formSendEmail, setFormSendEmail] = useState({
    username: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleClickSendEmail = async () => {
    let newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formSendEmail.username) newErrors.username = t("senmail_vUsername");
    if (!formSendEmail.email) {
      newErrors.email = t("senmail_vEmail");
    } else if (!emailRegex.test(formSendEmail.email)) {
      newErrors.email = t("senmail_vRegexEmail");
    }
    if (!formSendEmail.message) newErrors.message = t("senmail_vMessage");

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true); // Bật trạng thái loading
      try {
        const res = await apiSendEmail(formSendEmail);
        if (res.status === 200) {
          notification.success({
            message: "Send Email successfully!",
            duration: 2,
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Failed to send email. Please try again!",
          duration: 2,
          placement: "topRight",
        });
      } finally {
        setIsLoading(false); // Tắt trạng thái loading
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      whileInView={"visible"}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      variants={fadeIn}
      className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-16"
    >
      <div className="w-[80%] pvmax:max-dv:w-[85%]  pvmax:max-dv:mx-auto">
        <h2 className="text-4xl font-bold mb-6 text-primary qhd:text-[3rem]">
          {t("ContactFormTitle")}
        </h2>
        <p className="text-primary mb-8 ">{t("ContactFormSubtitle")}</p>
        <div className=" flex flex-col gap-4 text-primary">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2">
              <span className="text-2xl ">+</span>
              <span className="">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white p-6 lg:pr-0 rounded-lg">
        <div className="space-y-6">
          <div className="">
            <label
              htmlFor="name"
              className="block text-sm  font-medium text-primary mb-1 "
            >
              <p className=""> {t("ContactFormField_1")} *</p>
              {errors.username && (
                <p className="text-red-500 text-xs  ">{errors.username}</p>
              )}
            </label>
            <input
              type="text"
              id="name"
              onChange={(e) => {
                setFormSendEmail({
                  ...formSendEmail,
                  username: e.target.value,
                });
              }}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-700 focus:border-primary"
            />
          </div>

          <div className="">
            <label
              htmlFor="email"
              className="block text-sm  font-medium text-primary mb-1 "
            >
              <p className=""> {t("ContactFormField_2")} *</p>
              {errors.email && (
                <p className="text-red-500 text-xs ">{errors.email}</p>
              )}
            </label>
            <input
              type="email"
              onChange={(e) => {
                setFormSendEmail({
                  ...formSendEmail,
                  email: e.target.value,
                });
              }}
              id="email"
              required
              className="w-full px-4 py-2  border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-700 focus:border-primary"
            />
          </div>

          <div className="">
            <label
              htmlFor="message"
              className="block text-sm    font-medium text-primary mb-1 "
            >
              <p className=""> {t("ContactFormField_3")} *</p>
              {errors.message && (
                <p className="text-red-500 text-xs  ">{errors.message}</p>
              )}
            </label>
            <textarea
              id="message"
              onChange={(e) => {
                setFormSendEmail({
                  ...formSendEmail,
                  message: e.target.value,
                });
              }}
              required
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-700 focus:border-primary"
            />
          </div>

          <div className="flex justify-center">
            <button
              className={`w-full bg-primary  mx-auto text-white py-3 px-6 rounded-md ${
                isLoading ? "opacity-50 cursor-not-allowed" : "hover:opacity-70"
              } transition-colors duration-200`}
              onClick={() => {
                if (!isLoading) handleClickSendEmail();
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center space-x-2">
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    ></path>
                  </svg>
                  <span className="">{t("email_sending")}</span>
                </span>
              ) : (
                <p className=""> {t("SendMessage")}</p>
              )}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Contact = () => {
  const { t } = useTranslation();
  const fadeInUp = {
    hidden: { opacity: 0, y: 100 },
    visible: { opacity: 1, y: 0 },
  };
  const zoomIn = {
    hidden: { opacity: 0, scale: 0.5 },
    visible: { opacity: 1, scale: 1 },
  };
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  return (
    <div>
      <motion.div
        initial="hidden"
        whileInView={"visible"}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        variants={fadeIn}
        className="flex pt-[100px] max-w-[1140px] qhd:max-w-[60%]  mx-auto w-[85%] items-center"
      >
        <div
          className="ph:text-[3.375rem] max-w-[650px]   text-primary text-[3.75rem]  pvmax:max-dv:w-[85%]"
          style={{ fontWeight: 500, lineHeight: 1 }}
        >
          {t("ContactTitle")}
        </div>
      </motion.div>
      <motion.div
        initial="hidden"
        whileInView={"visible"}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        variants={fadeIn}
        className="flex mt-6 max-w-[1140px] qhd:max-w-[60%]   mx-auto w-[85%] items-center"
      >
        <div className="max-w-[540px]  text-primary ">
          {t("ContactSubtitle")}
        </div>
      </motion.div>
      <motion.div
        initial="hidden"
        whileInView={"visible"}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        variants={fadeInUp}
        className="pt-[4.375rem]"
      >
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3919.3118860705845!2d106.68245247593262!3d10.787407489362096!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31752f2d0021d0c7%3A0xe3d661ddfe81894f!2sAn%20Phu%20Plaza%20Serviced%20Apartment!5e0!3m2!1sen!2s!4v1757410490927!5m2!1sen!2s"
          width="100%"
          height="500"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Google Maps Location"
          className="qhd:h-[1000px]"
        ></iframe>
      </motion.div>
      <ContactInfo />
      <div className="max-w-[1140px] mx-auto w-[85%] py-10 qhd:max-w-[60%]">
        <ContactForm />
      </div>
    </div>
  );
};

export default Contact;
